import { dramas } from "@/mocks/dramas";
import { actors } from "@/mocks/actors";
import { news } from "@/mocks/news";

export type Drama = (typeof dramas)[number];
export type Actor = (typeof actors)[number];
export type NewsItem = (typeof news)[number];

export function getAllDramas(): Drama[] {
  return dramas;
}

export function getDramaById(id: string): Drama | undefined {
  return dramas.find((item) => item.id === id);
}

export function getAllActors(): Actor[] {
  return actors;
}

export function getActorById(id: string): Actor | undefined {
  return actors.find((item) => item.id === id);
}

export function getActorsByIds(ids: string[]): Actor[] {
  return ids
    .map((id) => actors.find((item) => item.id === id))
    .filter((item): item is Actor => Boolean(item));
}

export function getDramasByIds(ids: string[]): Drama[] {
  return ids
    .map((id) => dramas.find((item) => item.id === id))
    .filter((item): item is Drama => Boolean(item));
}

export function getNewsById(id: string): NewsItem | undefined {
  return news.find((item) => item.id === id);
}

export function getAllNews(): NewsItem[] {
  return [...news].sort((a, b) => (a.date < b.date ? 1 : -1));
}

export function getRelatedDramas(drama: Drama, limit = 4): Drama[] {
  return dramas
    .filter((item) => item.id !== drama.id)
    .sort((a, b) => {
      const aSameCategory = a.category === drama.category ? 1 : 0;
      const bSameCategory = b.category === drama.category ? 1 : 0;
      if (aSameCategory !== bSameCategory) return bSameCategory - aSameCategory;
      return b.heat - a.heat;
    })
    .slice(0, limit);
}

export function getDramasByActor(actorId: string): Drama[] {
  const actor = getActorById(actorId);
  if (!actor) return [];
  return getDramasByIds(actor.dramaIds);
}

export function searchDramas(keyword: string): Drama[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return dramas;
  const matchedActorIds = actors
    .filter((actor) => actor.name.toLowerCase().includes(key))
    .map((actor) => actor.id);
  return dramas.filter(
    (drama) =>
      drama.title.toLowerCase().includes(key) ||
      drama.category.toLowerCase().includes(key) ||
      drama.tags.some((tag) => tag.toLowerCase().includes(key)) ||
      drama.castIds.some((id) => matchedActorIds.includes(id)),
  );
}

export function getRanking(period: "week" | "month" | "all"): Drama[] {
  const factor = period === "week" ? 1 : period === "month" ? 0.82 : 0.66;
  return [...dramas]
    .map((drama, index) => ({
      drama,
      score: Math.round(drama.heat * factor - index * 12),
    }))
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.drama);
}

export function getRankingScore(drama: Drama, period: "week" | "month" | "all"): number {
  const factor = period === "week" ? 1 : period === "month" ? 0.82 : 0.66;
  return Math.round(drama.heat * factor);
}